import { Controller } from "@hotwired/stimulus"

// Marks a lesson complete once the learner reaches its end.
//
// "Reaching the end" is observed rather than inferred: a sentinel sits after the last
// block of the lesson, and the lesson counts as done when that sentinel comes into
// view. A lesson short enough to fit the viewport satisfies this on load, which is
// correct — there was no more of it to reach.
//
// The pager's Next is the second trigger, for a learner who decides they are finished
// with a lesson and moves on. Both are idempotent on the server, so firing twice is
// ordinary rather than exceptional.
export default class extends Controller {
  static targets = ["sentinel", "count", "tick"]
  static values = {
    url: String,
    lessonUuid: String,
    completed: Boolean
  }

  connect() {
    if (!this.hasSentinelTarget || this.completedValue) return

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.complete()
      },
      { rootMargin: "0px 0px -8px 0px" }
    )
    this.observer.observe(this.sentinelTarget)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // Wired to the pager's Next link. The navigation is left to proceed — the request is
  // fire-and-forget precisely so a slow round trip never delays the learner.
  completeAndContinue() {
    this.complete()
  }

  async complete() {
    if (this.requestSent || !this.lessonUuidValue) return
    this.requestSent = true
    this.observer?.disconnect()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ lesson_uuid: this.lessonUuidValue })
      })

      if (!response.ok) return

      const payload = await response.json()
      if (payload.success) this.render(payload)
    } catch {
      // A lesson that fails to record is retried on the learner's next visit; there is
      // nothing useful to say to them about it mid-scroll.
      this.requestSent = false
    }
  }

  render(payload) {
    this.countTargets.forEach((element) => {
      element.textContent = `${payload.completed_count} of ${payload.total_count}`
    })

    const completed = new Set(payload.completed_lesson_uuids)
    this.tickTargets.forEach((element) => {
      element.hidden = !completed.has(element.dataset.lessonUuid)
    })
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content ?? ""
  }
}
